import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { warehouseAPI, asinDatabaseAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Calendar, PackageCheck, Search, Pencil, Trash2 } from 'lucide-react';

export const ReceivedProducts = () => {
  const [received, setReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [eanInput, setEanInput] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  const [formData, setFormData] = useState({
    quantity: '',
    order_id: '',
  });

  const fetchReceived = async (date) => {
    setLoading(true);
    try {
      const response = await warehouseAPI.getReceived(date);
      setReceived(response.data);
    } catch (error) {
      toast.error('Failed to load received products');
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async () => {
    if (!eanInput.trim()) {
      toast.error('Please enter an EAN to search');
      return;
    }
    setLookupLoading(true);
    try {
      const response = await asinDatabaseAPI.lookup(eanInput.trim());
      setSelectedProduct(response.data);
      toast.success('Product found');
    } catch (error) {
      setSelectedProduct(null);
      toast.error('EAN not found in ASIN Database');
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => {
    fetchReceived(selectedDate);
  }, [selectedDate]);

  const resetFormState = () => {
    setFormData({ quantity: '', order_id: '' });
    setEanInput('');
    setSelectedProduct(null);
    setEditingEntry(null);
  };

  const openNewDialog = () => {
    resetFormState();
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (!selectedProduct) {
        toast.error('Find a product via EAN first');
        return;
      }

      const payload = {
        product_id: selectedProduct.id,
        ean: selectedProduct.ean,
        asin: selectedProduct.asin,
        name: selectedProduct.name || selectedProduct.sku || selectedProduct.ean,
        quantity: parseInt(formData.quantity),
        received_date: selectedDate,
        order_id: formData.order_id || null,
      };

      if (editingEntry) {
        await warehouseAPI.updateReceived(editingEntry.id, payload);
        toast.success('Received entry updated');
      } else {
        await warehouseAPI.addReceived(payload);
        toast.success('Received product recorded');
      }
      setDialogOpen(false);
      resetFormState();
      fetchReceived(selectedDate);
    } catch (error) {
      toast.error('Failed to save received product');
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEanInput(entry.ean);
    setSelectedProduct({
      id: entry.product_id,
      ean: entry.ean,
      asin: entry.asin,
      name: entry.name,
    });
    setFormData({
      quantity: entry.quantity.toString(),
      order_id: entry.order_id || '',
    });
    setDialogOpen(true);
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm('Delete this received entry and revert stock?')) return;
    try {
      await warehouseAPI.deleteReceived(entry.id);
      toast.success('Received entry deleted');
      fetchReceived(selectedDate);
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const totalReceived = received.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Layout>
      <div className="page-header flex justify-between items-start">
        <div>
          <h1 className="page-title" data-testid="received-products-title">Received Products</h1>
          <p className="page-subtitle">Track products received at your warehouse</p>
        </div>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              resetFormState();
            }
          }}
        >
          <DialogTrigger asChild>
            <Button data-testid="add-received-btn" onClick={openNewDialog}>
              <Plus size={16} className="mr-2" />
              Record Received
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingEntry ? 'Edit Received Product' : 'Record Received Product'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>EAN (lookup)</Label>
                <div className="flex gap-2">
                  <Input
                    value={eanInput}
                    onChange={(e) => setEanInput(e.target.value)}
                    placeholder="Scan or paste EAN"
                    data-testid="received-ean-input"
                  />
                  <Button type="button" onClick={handleLookup} disabled={lookupLoading}>
                    <Search size={16} className="mr-2" />
                    {lookupLoading ? 'Searching…' : 'Find'}
                  </Button>
                </div>
                {selectedProduct ? (
                  <div className="mt-3 rounded border border-dashed border-slate-300 p-3 text-sm">
                    <p className="font-medium">{selectedProduct.name || selectedProduct.sku || selectedProduct.ean}</p>
                    <p className="text-slate-500">ASIN: {selectedProduct.asin || 'N/A'}</p>
                    <p className="text-slate-500">EAN: {selectedProduct.ean}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500">Search an EAN to load product details.</p>
                )}
              </div>
              <div>
                <Label>Quantity Received</Label>
                <Input
                  type="number"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="100"
                  data-testid="received-quantity-input"
                />
              </div>
              <div>
                <Label>Order ID (Optional)</Label>
                <Input
                  value={formData.order_id}
                  onChange={(e) => setFormData({ ...formData, order_id: e.target.value })}
                  placeholder="Related order ID"
                  data-testid="received-order-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmit} data-testid="save-received-btn">Record</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Date Selector and Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="col-span-1">
          <CardContent className="p-4">
            <Label className="mb-2 block">Select Date</Label>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="flex-1"
                data-testid="date-selector"
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Items Received</p>
            <p className="text-3xl font-bold text-slate-900">{received.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Total Units</p>
            <p className="text-3xl font-bold text-slate-900">{totalReceived.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-3 bg-green-50 rounded-lg">
              <PackageCheck size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Status</p>
              <p className="font-medium text-green-600">Receiving Active</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card data-testid="received-products-card">
        <CardHeader>
          <CardTitle className="text-lg">Received on {selectedDate}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Name</TableHead>
                  <TableHead>EAN</TableHead>
                  <TableHead>ASIN</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Date Received</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {received.map((item, index) => (
                  <TableRow key={item.id || index} data-testid={`received-row-${index}`}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="font-mono text-xs">{item.ean}</TableCell>
                    <TableCell className="font-mono text-xs">{item.asin}</TableCell>
                    <TableCell className="text-right font-bold text-green-600">
                      +{Number(item.quantity).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">
                      {item.order_id ? item.order_id : '—'}
                    </TableCell>
                    <TableCell>{new Date(item.received_date).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEditEntry(item)}>
                          <Pencil size={16} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteEntry(item)}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {received.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No products received on {selectedDate}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
};
